import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PageHeader,
  DataTable,
  StatusBadge,
  Modal,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAlertSummary } from '../../hooks/use-alert-summary'
import { livenessFromLastSeen, relativeTime, type EnvironmentRow } from './fleet-helpers'
import { useTableColumns } from '../../hooks/use-table-columns'
import { SupportEmailCard } from './SupportEmailCard'

interface StatsResponse {
  readonly total: number
  readonly online: number
  readonly stale: number
  readonly expiringSoon: number
  readonly versionDistribution: readonly { readonly version: string | null; readonly count: number }[]
  readonly advertisedVersion: string | null
  readonly minVersion: string | null
}

const columns: readonly Column<EnvironmentRow>[] = [
  {
    key: 'owner',
    header: 'Customer',
    render: (r) => r.ownerOrganizationName ?? '—',
    sortable: true,
  },
  {
    key: 'name',
    header: 'Environment',
    render: (r) => <strong>{r.name}</strong>,
    sortable: true,
  },
  {
    key: 'liveness',
    header: 'Liveness',
    render: (r) => {
      if (r.provisioningStatus === 'awaiting_dns') return <StatusBadge status="warning" label="Awaiting DNS" />
      if (r.provisioningStatus === 'installing') return <StatusBadge status="in_progress" label="Installing" />
      return <StatusBadge status={livenessFromLastSeen(r.lastSeenAt)} />
    },
  },
  {
    key: 'currentVersion',
    header: 'Version',
    render: (r) => r.currentVersion ?? '—',
  },
  {
    key: 'lastSeenAt',
    header: 'Last seen',
    render: (r) => {
      if (r.provisioningStatus === 'awaiting_dns') return r.publicIp ?? '—'
      return relativeTime(r.lastSeenAt)
    },
  },
]

// medflow_admin-only cross-customer view. Shows every enrolled
// environment regardless of owner. The same GET /api/fleet/environments
// endpoint serves both customer_admin (scoped) and medflow_admin
// (unscoped) -- scoping is done server-side based on the caller's role.
export function FleetListPage() {
  const navigate = useNavigate()
  const alertSummary = useAlertSummary()
  const [rows, setRows] = useState<readonly EnvironmentRow[]>([])
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet')

  // Version advertisement / minimum version editing
  const [versionEditType, setVersionEditType] = useState<'advertised' | 'minimum' | null>(null)
  const [versionEditInput, setVersionEditInput] = useState('')
  const [versionEditSaving, setVersionEditSaving] = useState(false)
  const [versionEditError, setVersionEditError] = useState<string | null>(null)

  // Version drill-down modal
  const [drillVersion, setDrillVersion] = useState<string | null>(null)
  const [drillRows, setDrillRows] = useState<readonly EnvironmentRow[]>([])
  const [drillLoading, setDrillLoading] = useState(false)

  const [driftCount, setDriftCount] = useState(0)
  const [restartPendingCount, setRestartPendingCount] = useState(0)

  const loadData = useCallback(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      api.get<readonly EnvironmentRow[]>('/fleet/environments'),
      api.get<StatsResponse>('/fleet/stats'),
      api.get<readonly { configDriftDetectedAt: string | null; restartState: string | null }[]>(
        '/fleet/devices?limit=200',
      ),
    ])
      .then(([envRes, statsRes, devRes]) => {
        if (cancelled) return
        setRows(envRes.data ?? [])
        setStats(statsRes.data ?? null)
        setDriftCount((devRes.data ?? []).filter((d) => d.configDriftDetectedAt).length)
        setRestartPendingCount((devRes.data ?? []).filter((d) => d.restartState === 'pending').length)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load fleet')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    return loadData()
  }, [loadData])

  const openVersionEdit = (type: 'advertised' | 'minimum') => {
    const current = type === 'advertised' ? (stats?.advertisedVersion ?? '') : (stats?.minVersion ?? '')
    setVersionEditInput(current)
    setVersionEditError(null)
    setVersionEditType(type)
  }

  const closeVersionEdit = () => {
    setVersionEditType(null)
    setVersionEditInput('')
    setVersionEditError(null)
  }

  const saveVersionEdit = async () => {
    if (!versionEditType) return
    setVersionEditSaving(true)
    setVersionEditError(null)
    try {
      const endpoint =
        versionEditType === 'advertised' ? '/admin/fleet/update-advertisement' : '/admin/fleet/minimum-version'
      await api.patch(endpoint, { version: versionEditInput.trim() || null })
      closeVersionEdit()
      loadData()
    } catch (err) {
      setVersionEditError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setVersionEditSaving(false)
    }
  }

  const clearVersionEdit = async () => {
    if (!versionEditType) return
    setVersionEditSaving(true)
    setVersionEditError(null)
    try {
      const endpoint =
        versionEditType === 'advertised' ? '/admin/fleet/update-advertisement' : '/admin/fleet/minimum-version'
      await api.patch(endpoint, { version: null })
      closeVersionEdit()
      loadData()
    } catch (err) {
      setVersionEditError(err instanceof ApiError ? err.message : 'Clear failed')
    } finally {
      setVersionEditSaving(false)
    }
  }

  const openDrillDown = async (version: string) => {
    setDrillVersion(version)
    setDrillLoading(true)
    setDrillRows([])
    try {
      const res = await api.get<readonly EnvironmentRow[]>(
        `/admin/fleet/environments?version=${encodeURIComponent(version)}`,
      )
      setDrillRows(res.data ?? [])
    } catch {
      setDrillRows([])
    } finally {
      setDrillLoading(false)
    }
  }

  const statCardStyle = {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '1rem',
    minWidth: '160px',
  }

  const configCardStyle = {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    minWidth: '220px',
  }

  // Drill-down column: shows upgrade-required badge when env version < minVersion
  const drillColumns: readonly Column<EnvironmentRow>[] = [
    { key: 'name', header: 'Environment', render: (r) => <strong>{r.name}</strong>, sortable: true },
    { key: 'owner', header: 'Customer', render: (r) => r.ownerOrganizationName ?? '—', sortable: true },
    { key: 'lastSeenAt', header: 'Last seen', render: (r) => relativeTime(r.lastSeenAt) },
    {
      key: 'upgrade',
      header: 'Upgrade required',
      render: (r) => {
        if (!stats?.minVersion || !r.currentVersion) return <StatusBadge status="unknown" />
        const below = isVersionLt(r.currentVersion, stats.minVersion)
        return <StatusBadge status={below ? 'warning' : 'active'} />
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Fleet"
        subtitle="All customer environments"
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={() => navigate('/fleet/customers')} style={btnPrimary}>
              Customers
            </button>
          </>
        }
      />
      {alertSummary.activeCount > 0 && (
        <div
          onClick={() => navigate('/fleet/alerts')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate('/fleet/alerts')
          }}
          style={{
            padding: '0.6rem 1rem',
            marginBottom: '0.75rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            background: alertSummary.criticalCount > 0 ? '#fee2e2' : '#fef3c7',
            color: alertSummary.criticalCount > 0 ? '#dc2626' : '#92400e',
            border: `1px solid ${alertSummary.criticalCount > 0 ? '#fecaca' : '#fde68a'}`,
          }}
        >
          {alertSummary.activeCount} active alert{alertSummary.activeCount !== 1 ? 's' : ''}
          {alertSummary.criticalCount > 0 ? ` (${alertSummary.criticalCount} critical)` : ''} — click to view
        </div>
      )}
      {stats && (
        <>
          {/* Fleet health stat cards */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={statCardStyle}>
              <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>Total</div>
              <div style={{ fontSize: '28px', fontWeight: 600, color: colors.textHeading }}>{stats.total}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>Online</div>
              <div style={{ fontSize: '28px', fontWeight: 600, color: '#16a34a' }}>{stats.online}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>
                Stale / offline
              </div>
              <div style={{ fontSize: '28px', fontWeight: 600, color: '#dc2626' }}>{stats.stale}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase' }}>
                Expiring &lt; 30d
              </div>
              <div style={{ fontSize: '28px', fontWeight: 600, color: '#FF9500' }}>{stats.expiringSoon}</div>
            </div>
            {driftCount > 0 && (
              <div
                onClick={() => navigate('/fleet/devices')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate('/fleet/devices')
                }}
                style={{
                  ...statCardStyle,
                  cursor: 'pointer',
                  background: '#fef3c7',
                  borderColor: '#fde68a',
                }}
              >
                <div style={{ fontSize: '12px', color: '#92400e', textTransform: 'uppercase' }}>Config Drift</div>
                <div style={{ fontSize: '28px', fontWeight: 600, color: '#92400e' }}>{driftCount}</div>
              </div>
            )}
            {restartPendingCount > 0 && (
              <div
                onClick={() => navigate('/fleet/devices')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate('/fleet/devices')
                }}
                style={{
                  ...statCardStyle,
                  cursor: 'pointer',
                  background: '#ede9fe',
                  borderColor: '#ddd6fe',
                }}
              >
                <div style={{ fontSize: '12px', color: '#6d28d9', textTransform: 'uppercase' }}>Restart Pending</div>
                <div style={{ fontSize: '28px', fontWeight: 600, color: '#6d28d9' }}>{restartPendingCount}</div>
              </div>
            )}
          </div>

          {/* Version management config cards */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={configCardStyle}>
              <div>
                <div
                  style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '2px' }}
                >
                  Edge app advertised version
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textHeading }}>
                  {stats.advertisedVersion ?? '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => openVersionEdit('advertised')}
                style={{ ...btnSecondary, fontSize: '12px', padding: '0.25rem 0.5rem' }}
              >
                Edit
              </button>
            </div>
            <div style={configCardStyle}>
              <div>
                <div
                  style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '2px' }}
                >
                  Edge app minimum version
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textHeading }}>
                  {stats.minVersion ?? '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => openVersionEdit('minimum')}
                style={{ ...btnSecondary, fontSize: '12px', padding: '0.25rem 0.5rem' }}
              >
                Edit
              </button>
            </div>
            <SupportEmailCard />
          </div>

          {/* Version distribution histogram — each row is clickable for drill-down */}
          {stats.versionDistribution.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  marginBottom: '0.5rem',
                }}
              >
                Version distribution
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {stats.versionDistribution.map((entry) => (
                  <button
                    key={entry.version ?? 'unknown'}
                    type="button"
                    onClick={() => entry.version && openDrillDown(entry.version)}
                    disabled={!entry.version}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      padding: '0.3rem 0.75rem',
                      cursor: entry.version ? 'pointer' : 'default',
                      textAlign: 'left',
                      width: 'fit-content',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: colors.textHeading,
                        minWidth: '100px',
                      }}
                    >
                      {entry.version ?? 'unknown'}
                    </span>
                    <span style={{ fontSize: '12px', color: colors.textMuted }}>
                      {entry.count} env{entry.count !== 1 ? 's' : ''}
                    </span>
                    {entry.version && <span style={{ fontSize: '11px', color: colors.accent }}>view</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          keyField="id"
          onRowClick={(r) =>
            r.provisioningStatus === 'awaiting_dns'
              ? navigate(`/fleet/provision?resume=${r.id}`)
              : navigate(`/fleet/${r.id}`)
          }
          emptyMessage="No environments enrolled yet. Use /fleet/customers to provision the first customer."
          {...tableColumnProps}
        />
      )}

      {/* Version edit modal (advertised or minimum) */}
      <Modal
        isOpen={versionEditType !== null}
        onClose={closeVersionEdit}
        title={versionEditType === 'advertised' ? 'Edit edge app advertised version' : 'Edit edge app minimum version'}
        width="420px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ fontSize: '13px', color: colors.textMuted, margin: 0 }}>
            {versionEditType === 'advertised'
              ? 'This version tag is broadcast to all edges via the heartbeat response. Leave blank or click Clear to remove the advertisement.'
              : 'Edges running below this version receive an upgrade-required signal. Leave blank or click Clear to remove the floor.'}
          </p>
          <input
            type="text"
            value={versionEditInput}
            onChange={(e) => setVersionEditInput(e.target.value)}
            placeholder="e.g. 0.2.21"
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              padding: '0.4rem 0.75rem',
              fontSize: '14px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          {versionEditError && <div style={{ fontSize: '12px', color: colors.danger }}>{versionEditError}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={clearVersionEdit} disabled={versionEditSaving} style={btnSecondary}>
              Clear
            </button>
            <button type="button" onClick={closeVersionEdit} disabled={versionEditSaving} style={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveVersionEdit} disabled={versionEditSaving} style={btnPrimary}>
              {versionEditSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Version drill-down modal */}
      <Modal
        isOpen={drillVersion !== null}
        onClose={() => setDrillVersion(null)}
        title={`Environments on ${drillVersion ?? ''}`}
        width="700px"
      >
        {drillLoading ? (
          <div style={{ color: colors.textMuted }}>Loading...</div>
        ) : (
          <DataTable
            columns={drillColumns}
            rows={drillRows}
            keyField="id"
            emptyMessage="No environments found on this version."
          />
        )}
      </Modal>
    </div>
  )
}

// Client-side version comparison for the drill-down upgrade badge.
// Strips non-semver suffixes (e.g. "0.2.1d0" -> "0.2.1") and compares
// segment-by-segment. Returns true if a < b.
function isVersionLt(a: string, b: string): boolean {
  const normalize = (v: string) => v.replace(/[^0-9.].*$/, '')
  const aParts = normalize(a).split('.').map(Number)
  const bParts = normalize(b).split('.').map(Number)
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const av = aParts[i] ?? 0
    const bv = bParts[i] ?? 0
    if (av < bv) return true
    if (av > bv) return false
  }
  return false
}
